import React, { useEffect, useState } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Badge,
  Spinner,
  Alert,
  InputGroup,
  InputGroupText,
  CardFooter
} from 'reactstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Breadcrumbs from "components/Common/Breadcrumb";
import { fetchTryout } from '../../../services/tryoutService';
import {
  FiSearch,
  FiAlertCircle,
  FiAward,
  FiZap,
  FiArrowRight,
  FiTag,
  FiFileText,
  FiClock,
  FiArrowLeft
} from 'react-icons/fi';

const DetailPembahasan = () => {
  document.title = "Daftar Tryout";
  const location = useLocation();
  const navigate = useNavigate();

  const [tryouts, setTryouts] = useState([]);
  const [filteredTryouts, setFilteredTryouts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [selectedJenisSeleksi, setSelectedJenisSeleksi] = useState('');
  // === STATE BARU UNTUK id_bidang ===
  const [selectedIdBidang, setSelectedIdBidang] = useState('');

  const [jenisSeleksiNotFound, setJenisSeleksiNotFound] = useState(false);
  // === STATE BARU UNTUK idBidangNotFound (opsional, sesuaikan jika perlu validasi) ===
  const [idBidangNotFound, setIdBidangNotFound] = useState(false);


  const [noResultsFromSearch, setNoResultsFromSearch] = useState(false);
  const [dataTryoutsEmpty, setDataTryoutsEmpty] = useState(false);

  // === STATE UNTUK MENANDAI PROSES URL SELESAI ===
  const [urlParamsProcessed, setUrlParamsProcessed] = useState(false);

  const validJenisSeleksiOptions = [
    'CPNS', 'PPPK', 'BUMN', 'Sekolah Kedinasan', 'TNI', 'POLRI'
  ];

  // Effect 1: Mengatur `selectedJenisSeleksi` dan `selectedIdBidang` dari URL dan menandai proses selesai
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jenisSeleksiFromUrl = params.get('jenis_seleksi');
    const idBidangFromUrl = params.get('id_bidang'); // Mengambil id_bidang dari URL

    // Proses jenis_seleksi
    if (jenisSeleksiFromUrl) {
      if (validJenisSeleksiOptions.includes(jenisSeleksiFromUrl)) {
        setSelectedJenisSeleksi(jenisSeleksiFromUrl);
        setJenisSeleksiNotFound(false);
      } else {
        setSelectedJenisSeleksi('');
        setJenisSeleksiNotFound(true);
      }
    } else {
      setSelectedJenisSeleksi('');
      setJenisSeleksiNotFound(false);
    }

    // Proses id_bidang
    if (idBidangFromUrl) {
      // Anda mungkin ingin menambahkan validasi untuk id_bidang di sini,
      // misalnya memastikan itu adalah angka. Untuk sementara, kita hanya menyimpannya.
      setSelectedIdBidang(idBidangFromUrl);
      setIdBidangNotFound(false); // Atur ini berdasarkan validasi jika ada
    } else {
      setSelectedIdBidang('');
      setIdBidangNotFound(false);
    }

    // === SETELAH MEMPROSES URL, TANDAI SELESAI ===
    setUrlParamsProcessed(true);
  }, [location.search, validJenisSeleksiOptions]);


  // Effect 2: Debounce searchTerm
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm]);

  // Effect 3: Memanggil API saat filter (jenis seleksi, id bidang, atau pencarian) berubah
  // Hanya panggil jika urlParamsProcessed sudah TRUE
  useEffect(() => {
    if (!urlParamsProcessed) {
      // Jika proses parsing URL belum selesai, jangan panggil API
      return;
    }

    // Jika jenis seleksi atau id bidang tidak ditemukan (jika Anda mengimplementasikan validasi untuk id_bidang)
    if (jenisSeleksiNotFound || idBidangNotFound) {
      setLoading(false);
      setTryouts([]);
      setFilteredTryouts([]);
      setDataTryoutsEmpty(true);
      setNoResultsFromSearch(false);
      setError(null);
      return;
    }

    const getTryoutsData = async () => {
      setLoading(true);
      setError(null);
      setDataTryoutsEmpty(false);
      setNoResultsFromSearch(false);

      try {
        const filters = {};
        if (selectedJenisSeleksi) {
          filters.jenis_seleksi = selectedJenisSeleksi;
        }
        // === TAMBAHKAN id_bidang KE FILTER ===
        if (selectedIdBidang) {
          filters.id_bidang = selectedIdBidang;
        }
        if (debouncedSearchTerm) {
          filters.search = debouncedSearchTerm;
        }

        // Asumsi fetchTryout bisa menerima parameter id_bidang
        const response = await fetchTryout(1, 10, filters);

        if (response.success) {
          if (Array.isArray(response.data) && response.data.length > 0) {
            const sortedData = [...response.data].sort((a, b) => {
              if (a.tipe === 'GRATIS' && b.tipe !== 'GRATIS') {
                return -1;
              }
              if (a.tipe !== 'GRATIS' && b.tipe === 'GRATIS') {
                return 1;
              }
              return 0;
            });
            setTryouts(sortedData);
            setFilteredTryouts(sortedData);
          } else if (response.data === null || (Array.isArray(response.data) && response.data.length === 0)) {
            setTryouts([]);
            setFilteredTryouts([]);
            if (debouncedSearchTerm) {
              setNoResultsFromSearch(true);
            } else {
              setDataTryoutsEmpty(true);
            }
          } else {
            setTryouts([]);
            setFilteredTryouts([]);
            setError('Format data tryout tidak sesuai dari API.');
          }
        } else {
          setTryouts([]);
          setFilteredTryouts([]);
          setError(response.message || 'Gagal mengambil data tryout dari server.');
        }
      } catch (err) {
        setTryouts([]);
        setFilteredTryouts([]);
        setError('Terjadi kesalahan jaringan saat mengambil data tryout.');
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    // Tambahkan selectedIdBidang ke dependency array
    getTryoutsData();
  }, [selectedJenisSeleksi, selectedIdBidang, debouncedSearchTerm, jenisSeleksiNotFound, idBidangNotFound, urlParamsProcessed]);


  const getBadgeColor = (tipe) => {
    switch (tipe) {
      case 'GRATIS': return 'bg-success-subtle text-success';
      case 'PREMIUM': return 'bg-warning-subtle text-warning';
      default: return 'bg-secondary-subtle text-secondary';
    }
  };

  const getBadgeIcon = (tipe) => {
    switch (tipe) {
      case 'GRATIS': return <FiAward size={10} className="me-1" />;
      case 'PREMIUM': return <FiZap size={10} className="me-1" />;
      default: return null;
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  // --- Conditional Renders for Different States ---

  // Jika belum selesai memproses parameter URL atau sedang loading data, tampilkan spinner.
  if (!urlParamsProcessed || loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
          </div>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
            <Spinner color="primary" />
            <span className="ms-2">Memuat daftar tryout...</span>
          </div>
        </Container>
      </div>
    );
  }

  // --- ERROR HANDLING DAN PESAN KOSONG TETAP SAMA SETELAH INI ---
  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
            <Breadcrumbs title="Tryout" breadcrumbItem="Daftar Tryout" />
          </div>
          <Alert color="danger">
            <FiAlertCircle className="me-2" size={18} />
            {error}
            <Button color="danger" outline size="sm" className="ms-3" onClick={() => window.location.reload()}>
              Coba Lagi
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  if (jenisSeleksiNotFound) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
            <Breadcrumbs title="Tryout" breadcrumbItem="Daftar Tryout" />
          </div>
          <Alert color="warning" className="text-center py-5">
            <FiAlertCircle size={30} className="mb-3" />
            <h4>Jenis Seleksi Tidak Ditemukan!</h4>
            <p>Parameter `jenis_seleksi` di URL tidak valid atau tidak ada.</p>
            <Link to="/paket-simulasi">
              <Button color="primary" className="mt-3">
                Lihat Semua Tryout
              </Button>
            </Link>
          </Alert>
        </Container>
      </div>
    );
  }

  // === Tambahkan kondisi jika idBidangNotFound (jika Anda mengimplementasikan validasi) ===
  // Contoh:
  // if (idBidangNotFound) {
  //   return (
  //     <div className="page-content">
  //       <Container fluid>
  //         <div className="d-flex align-items-center mb-3">
  //           <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
  //             <FiArrowLeft className="me-2" /> Kembali
  //           </Button>
  //           <Breadcrumbs title="Tryout" breadcrumbItem="Daftar Tryout" />
  //         </div>
  //         <Alert color="warning" className="text-center py-5">
  //           <FiAlertCircle size={30} className="mb-3" />
  //           <h4>ID Bidang Tidak Ditemukan!</h4>
  //           <p>Parameter `id_bidang` di URL tidak valid.</p>
  //           <Link to="/paket-simulasi">
  //             <Button color="primary" className="mt-3">
  //               Lihat Semua Tryout
  //             </Button>
  //           </Link>
  //         </Alert>
  //       </Container>
  //     </div>
  //   );
  // }


  if (noResultsFromSearch && searchTerm) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
          </div>
          <Alert color="info" className="text-center py-3">
            <FiAlertCircle className="me-2" size={18} />
            Tidak ditemukan tryout dengan nama "{searchTerm}".
          </Alert>
          <div className="text-center mt-3">
            <Button
              color="secondary"
              onClick={() => setSearchTerm('')}
            >
              Reset Pencarian
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  if (dataTryoutsEmpty && !searchTerm && !jenisSeleksiNotFound && !idBidangNotFound) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="d-flex align-items-center mb-3">
            <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
              <FiArrowLeft className="me-2" /> Kembali
            </Button>
            <Breadcrumbs title="Tryout" breadcrumbItem="Daftar Tryout" />
          </div>
          <Alert color="info" className="text-center py-3">
            Tidak ditemukan tryout yang sesuai dengan kriteria Anda. Coba kata kunci atau filter lain!
          </Alert>
          <div className="text-center mt-3">
            <Button
              tag={Link}
              to="/paket-simulasi"
              color="secondary"
            >
              Reset Filter dan Lihat Semua Simulasi
            </Button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <div className="d-flex align-items-center mb-3">
          <Button color="secondary" onClick={handleGoBack} className="me-3 d-flex align-items-center">
            <FiArrowLeft className="me-2" /> Kembali
          </Button>
        </div>

        {selectedJenisSeleksi && (
          <Row className="mb-3">
            <Col xs={12} className="text-center">
              <h5 className="mb-0">
                Menampilkan Tryout untuk:{' '}
                <Badge color="primary" className="ms-2 py-2 px-3 fw-bold">
                  {selectedJenisSeleksi}
                </Badge>

              </h5>
            </Col>
          </Row>
        )}

        <Row className="mb-4">
          <Col lg={8} md={10} className="mx-auto">
            <InputGroup className="search-input-group-minimal">
              <InputGroupText className="search-icon-minimal">
                <FiSearch />
              </InputGroupText>
              <input
                type="text"
                className="form-control search-field-minimal"
                placeholder="Cari tryout berdasarkan nama..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
        </Row>

        <Row className="gy-4 mb-5">
          {Array.isArray(filteredTryouts) && filteredTryouts.map((tryout) => {
            return (
              <Col key={tryout.id_tryout} xl={4} lg={6} md={6}>
                <Card className="tryout-card-v17 shadow-sm h-100">
                  <CardBody className="p-3 text-center d-flex flex-column justify-content-between">
                    <div className="d-flex align-items-center justify-content-center mb-2 flex-wrap">
                      <h6 className="fw-bold text-dark text-center tryout-title-v17 mb-0 me-2">
                        {tryout.nama_tryout}
                      </h6>
                      <Badge
                        color=""
                        className={`badge-tipe-ultrasmall text-uppercase ${getBadgeColor(tryout.tipe)}`}>
                        {getBadgeIcon(tryout.tipe)} {tryout.tipe}
                      </Badge>
                    </div>

                    <div className="d-flex flex-wrap justify-content-center gap-3 mb-2">
                      {tryout.jumlah_soal != null && (
                        <div className="info-item-small">
                          <FiFileText className="info-icon-small me-1" />
                          <span>{tryout.jumlah_soal} Soal</span>
                        </div>
                      )}
                      {tryout.durasi_menit != null && (
                        <div className="info-item-small">
                          <FiClock className="info-icon-small me-1" />
                          <span>{tryout.durasi_menit} Menit</span>
                        </div>
                      )}
                    </div>

                    {tryout.nama_bidang && (
                      <small className="text-muted d-block mb-2">
                        <FiTag className="me-1" /> {tryout.nama_bidang}
                      </small>
                    )}

                  </CardBody>
                  <CardFooter className="bg-white border-top text-center p-2">
                    <Button tag={Link} to={`/pembahasan-soal?id_tryout=${tryout.id_tryout}`} color="primary" className="d-block mx-auto w-100 btn-action-v17">
                      Lihat Pembahasan <FiArrowRight className="ms-1" />
                    </Button>
                  </CardFooter>
                </Card>
              </Col>
            );
          })}
        </Row>
      </Container>

      <style>
        {`
          /* Universal Box Sizing */
          *, *::before, *::after {
            box-sizing: border-box;
          }

          /* Search Input Styling */
          .search-input-group-minimal {
            border-radius: 10px;
            overflow: hidden;
            box-shadow: 0 2px 10px rgba(0,0,0,0.04);
            border: 1px solid #e0e0e0;
          }

          .search-icon-minimal {
            background-color: #ffffff;
            border: none;
            color: #6c757d;
            font-size: 1.1rem;
            padding: 0.75rem 1rem;
          }
          .search-field-minimal:focus {
            box-shadow: none;
            border-color: transparent;
            outline: none;
          }

          /* Filter Button Styling (relevant if you re-add buttons) */
          .filter-button-v2 {
            border-radius: 20px;
            padding: 0.5rem 1.2rem;
            font-size: 0.9rem;
            font-weight: 500;
            transition: all 0.2s ease;
          }
          .filter-button-v2:hover {
            transform: translateY(-1px);
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          }
          .filter-button-v2.btn-primary {
            background-color: #007bff;
            border-color: #007bff;
            color: #fff;
            box-shadow: 0 3px 10px rgba(0,123,255,0.2);
          }
          .filter-button-v2.btn-light.outline {
            border: 1px solid #ced4da;
            color: #6c757d;
            background-color: #f8f9fa;
          }


          /* Card Styling - V17 Enhanced */
          .tryout-card-v17 {
            transition: all 0.3s ease-in-out;
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 15px rgba(0,0,0,0.08);
            background-color: #ffffff;
            border: none;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }

          .tryout-card-v17:hover {
            transform: translateY(-5px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
          }

          .tryout-title-v17 {
            font-size: 1.15rem; /* Sedikit diperbesar dari 1.1rem */
            font-weight: 700;
            line-height: 1.3;
            color: #343a40;
            white-space: normal; /* Ini akan membuat teks melipat ke baris baru jika terlalu panjang */
            word-wrap: break-word; /* Memastikan kata yang panjang terpotong jika perlu */
            max-width: 100%; /* Biarkan mengambil lebar penuh */
            flex-shrink: 1; /* Biarkan mengecil jika perlu */
          }
          .d-flex.align-items-center.justify-content-center.flex-wrap {
            margin-bottom: 0.5rem !important; /* Kurangi margin bawah agar lebih rapat */
          }


          /* Badge styling yang lebih kecil dan cantik */
          .badge-tipe-ultrasmall {
            font-size: 0.7rem !important;
            font-weight: 600;
            padding: 0.25em 0.5em !important;
            border-radius: 4px;
            display: inline-flex;
            align-items: center;
            line-height: 1;
            vertical-align: middle;
            letter-spacing: 0.5px;
            box-shadow: 0 1px 3px rgba(0,0,0,0.08);
            transition: all 0.2s ease;
            flex-shrink: 0; /* Pastikan badge tidak mengecil */
          }
          .badge-tipe-ultrasmall svg {
            font-size: 0.8em;
            margin-right: 0.25em;
          }

          /* Info item untuk jumlah soal dan durasi */
          .info-item-small {
            display: flex;
            align-items: center;
            font-size: 0.8rem; /* Ukuran font info item */
            color: #555;
            white-space: nowrap;
          }
          .info-item-small .info-icon-small {
            font-size: 0.9rem;
            color: #888;
            margin-right: 4px;
          }


          .card-footer {
            background-color: #fcfcfc !important;
            border-top: 1px solid #eeeeee !important;
            padding: 15px !important;
            border-bottom-left-radius: 12px;
            border-bottom-right-radius: 12px;
          }

          /* Button Action */
          .btn-action-v17 {
            font-size: 0.95rem;
            font-weight: 600;
            padding: 0.6rem 1.2rem;
            border-radius: 8px;
            background-color: #007bff;
            border-color: #007bff;
            transition: all 0.2s ease;
            box-shadow: 0 3px 10px rgba(0,123,255,0.2);
          }

          .btn-action-v17:hover {
            background-color: #0056b3;
            border-color: #0056b3;
            transform: translateY(-2px);
            box-shadow: 0 5px 15px rgba(0,123,255,0.3);
          }

          .btn-action-v17 .ms-1 {
            margin-left: 0.4rem !important;
            font-size: 1rem;
          }

          .h-100 {
            display: flex;
            flex-direction: column;
          }

          @media (max-width: 767.98px) {
            .search-input-group-minimal {
              width: 100%;
            }
            .filter-button-v2 {
                flex: 1 1 auto;
                max-width: 48%;
            }
            .info-item-small {
                font-size: 0.75rem;
            }
            .badge-tipe-ultrasmall {
                font-size: 0.65rem !important;
            }
            .tryout-title-v17 {
                font-size: 1rem;
                max-width: 100%; /* Biarkan mengambil lebar penuh */
            }
          }
        `}
      </style>
    </div>
  );
};

export default DetailPembahasan;